import { computed, ref } from "vue";
import { defineStore } from "pinia";
import type { Incident } from "@entity/Incident";

export interface IncidentSelectionCommand {
  id: string;
  seq: number;
}

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }

  return String(err);
}

export const useIncidentStore = defineStore("incident", () => {
  const incidents = ref<Incident[]>([]);
  const activeLabelInstanceId = ref<number | null>(null);
  const selectionCommand = ref<IncidentSelectionCommand | null>(null);
  const selectionSequence = ref(0);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const hasIncidents = computed(() => incidents.value.length > 0);
  const hasActiveSelection = computed(() =>
    activeLabelInstanceId.value !== null || selectionCommand.value !== null);

  function setIncidents(nextIncidents: Incident[]): void {
    incidents.value = nextIncidents;
  }

  async function fetchIncidents(fetcher: () => Promise<Incident[]>): Promise<void> {
    error.value = null;
    isLoading.value = true;

    try {
      const nextIncidents = await fetcher();
      setIncidents(Array.isArray(nextIncidents) ? nextIncidents : []);
    } catch (err) {
      error.value = getErrorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  function applySelectionCommand(command: IncidentSelectionCommand | null | undefined): void {
    if (!command || !command.id) {
      return;
    }

    selectionCommand.value = command;
  }

  function selectByAnchor(anchorId: string): void {
    if (!anchorId) {
      return;
    }

    selectionSequence.value += 1;
    selectionCommand.value = {
      id: anchorId,
      seq: selectionSequence.value,
    };
  }

  function setActiveLabelInstance(instanceId: number | null): void {
    activeLabelInstanceId.value = instanceId;
  }

  function resetSelectionState(): void {
    activeLabelInstanceId.value = null;
    selectionCommand.value = null;
    selectionSequence.value = 0;
  }

  function $reset(): void {
    incidents.value = [];
    resetSelectionState();
    isLoading.value = false;
    error.value = null;
  }

  return {
    incidents,
    activeLabelInstanceId,
    selectionCommand,
    isLoading,
    error,
    hasIncidents,
    hasActiveSelection,
    setIncidents,
    fetchIncidents,
    applySelectionCommand,
    selectByAnchor,
    setActiveLabelInstance,
    $reset,
    resetSelectionState,
  };
});

